// Shared citation rendering for assistant/brief output: turns `[N]` markers in
// Markdown into clickable number-badge citations grouped by document, and a
// references list grouped by document. Used by the Research Assistant
// (ChatMessage) and the Brief tab so both render citations identically.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SourceReference } from '../../types/api';
import { CitationExcerpt } from './CitationExcerpt';

const CITATION_REGEX = /\[(\d+(?:,\s*\d+)*)\]/g;

const parseCitationNumbers = (raw: string): number[] =>
  raw
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));

/** Extract all unique cited numbers from the response text. */
export const extractCitedNumbers = (text: string): number[] => {
  const cited = new Set<number>();
  let m: RegExpExecArray | null;
  const re = new RegExp(CITATION_REGEX.source, 'g');
  while ((m = re.exec(text)) !== null) {
    parseCitationNumbers(m[1]).forEach((n) => cited.add(n));
  }
  return Array.from(cited).sort((a, b) => a - b);
};

/**
 * Canonical form of a citing sentence used to key per-claim highlight matches:
 * citation markers and markdown decoration stripped, whitespace collapsed,
 * lowercased. Must produce identical keys at enrichment and render time.
 */
export const normalizeClaimText = (text: string): string =>
  text
    .replace(/\[(?:\d+,\s*)*\d+\]/g, '')
    .replace(/[#*_>`]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** The sentence surrounding a citation marker at `pos` within a text node. */
export const sentenceAround = (text: string, pos: number): string => {
  let start = 0;
  for (let i = pos - 1; i >= 0; i--) {
    if ('.!?\n'.includes(text[i])) {
      start = i + 1;
      break;
    }
  }
  let end = text.length;
  for (let i = pos; i < text.length; i++) {
    if ('.!?\n'.includes(text[i])) {
      end = i + 1;
      break;
    }
  }
  return text.slice(start, end).trim();
};

// A leading "-- h1 > h2 > h3 --" line is a heading breadcrumb embedded in the
// chunk text. Split it off so the excerpt can show it as an italic section path
// (without the -- markers) above the body.
const SECTION_BREADCRUMB_RE = /^\s*--\s*(.+?)\s*--\s*$/;

export const parseSectionBreadcrumb = (
  text: string,
): { section: string | null; body: string } => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  const match = i < lines.length ? SECTION_BREADCRUMB_RE.exec(lines[i].trim()) : null;
  // Any leading "-- … --" line is the chunk's heading breadcrumb, whether it is
  // a full path ("Chapter > Section") or a single heading.
  if (match) {
    const body = lines
      .slice(i + 1)
      .join('\n')
      .replace(/^\n+/, '');
    return { section: match[1].trim(), body };
  }
  return { section: null, body: text };
};

/**
 * The chunk and spans to show for the sentence being hovered. A document's
 * chunks are combined under one citation number, so the variants are searched
 * too and whichever chunk supports this claim is the one displayed.
 */
const resolveForClaim = (
  source: SourceReference,
  claim: string | undefined,
): {
  source: SourceReference;
  matches?: Array<{ start: number; end: number; matchedText?: string }>;
} => {
  const candidates = [source, ...(source.variants || [])];
  const key = claim ? normalizeClaimText(claim) : '';
  if (key) {
    for (const candidate of candidates) {
      // Enrichment splits sentences in the markdown, where a soft line-wrap
      // ends a "sentence"; the rendered text the card sees has those wraps
      // collapsed. Containment matches the two forms of the same sentence.
      const hit = (candidate.claimMatches || []).find(
        (e) =>
          e.claim === key ||
          (e.claim.length > 24 && (key.includes(e.claim) || e.claim.includes(key))),
      );
      if (hit) return { source: candidate, matches: hit.matches };
    }
  }
  // A source cited once has no ambiguity about which claim its spans support.
  const only = source.claimMatches?.length === 1 ? source.claimMatches[0].matches : undefined;
  return { source, matches: only ?? source.semanticMatches };
};

const InlineCitation: React.FC<{
  num: number;
  source?: SourceReference;
  onClick?: (source: SourceReference) => void;
  claim?: string;
  // Ask the owner to highlight this source now (hover on an un-enriched
  // excerpt). The card re-renders from state, so it fills in while open.
  onRequestHighlight?: (source: SourceReference) => void;
}> = ({ num, source, onClick, claim, onRequestHighlight }) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [card, setCard] = useState<{ top: number; left: number } | null>(null);
  // The excerpt is clipped until the reader asks for the rest; expanding turns
  // the body into a scroll area (with its scrollbar) rather than a long card.
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Whether this card's on-screen placement has been settled for this open.
  const placedRef = useRef(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (source && onClick) onClick(source);
  };

  const shown = source ? resolveForClaim(source, claim) : null;
  const { section: breadcrumb, body: excerptBody } = parseSectionBreadcrumb(
    shown?.source.text || '',
  );

  const show = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (!source) return;
    // Nothing highlighted for this claim yet — kick off a pass for it.
    if (!shown?.matches?.length && onRequestHighlight) onRequestHighlight(source);
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    // Provisional spot below the badge; the layout effect below flips it above
    // once the real height is known and there is not room underneath.
    const CARD_W = 360;
    placedRef.current = false;
    setCard({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - CARD_W - 12) });
  };
  const scheduleHide = () => {
    hideTimer.current = setTimeout(() => {
      setCard(null);
      setExpanded(false);
      placedRef.current = false;
    }, 120);
  };

  // Is there more text than the collapsed height shows? Measured after the
  // card renders, so "Show more" only appears when it would do something.
  useEffect(() => {
    const el = bodyRef.current;
    if (!card || !el) return;
    setClipped(el.scrollHeight > el.clientHeight + 4);
  }, [card, excerptBody, expanded]);

  // Highlight enrichment re-renders these badges constantly. If the pointer is
  // already resting on one, no pointer event fires for the new node, so the
  // card would only appear after the user moved (or clicked). Re-open it here.
  useEffect(() => {
    if (card || !ref.current) return;
    if (ref.current.matches(':hover')) show();
  });

  // Keep the card on screen: flip above the citation when it would overflow
  // the bottom. Runs once per open (placedRef), never in a setCard→effect→
  // setCard loop, which froze the page.
  useLayoutEffect(() => {
    const el = cardRef.current;
    const anchor = ref.current;
    if (!card || !el || !anchor || placedRef.current) return;
    placedRef.current = true;
    const height = el.offsetHeight;
    const a = anchor.getBoundingClientRect();
    const room = window.innerHeight - a.bottom - 12;
    if (height > room && a.top > room) {
      setCard({ top: Math.max(8, a.top - height - 6), left: card.left });
    }
  }, [card]);

  return (
    <>
      <a
        ref={ref}
        href="#"
        className="ai-summary-citation"
        onClick={handleClick}
        // mouseover (not mouseenter) so the card also opens when the badge is
        // re-rendered under a stationary cursor — enrichment re-renders these
        // constantly, and mouseenter would not fire again until you left and
        // returned. mousemove is the safety net for that same case.
        onMouseOver={show}
        onMouseMove={() => {
          if (!card) show();
        }}
        onFocus={show}
        onMouseLeave={scheduleHide}
        onBlur={scheduleHide}
      >
        {num}
      </a>
      {card &&
        shown &&
        createPortal(
          <div
            ref={cardRef}
            className="citation-hover-card"
            style={{ top: card.top, left: card.left }}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
          >
            <div className="citation-hover-title">{shown.source.title || `Source ${num}`}</div>
            {typeof shown.source.page === 'number' && (
              <div className="citation-hover-meta">Page {shown.source.page}</div>
            )}
            {shown.source.text && (
              <div
                ref={bodyRef}
                className={`citation-hover-excerpt${
                  expanded ? ' citation-hover-excerpt-open' : ''
                }`}
              >
                {breadcrumb && <div className="citation-hover-section">{breadcrumb}</div>}
                {!shown.matches?.length && shown.source.claimMatches === undefined && (
                  <div className="citation-hover-pending">
                    <span className="brief-spinner citation-hover-spinner" />
                    Highlighting relevant text …
                  </div>
                )}
                <CitationExcerpt text={excerptBody} claim={claim} matches={shown.matches} />
              </div>
            )}
            {shown.source.text && (clipped || expanded) && (
              <button
                type="button"
                className="citation-hover-more"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

/** Split a text string on citation patterns and return mixed text + badges. */
function replaceCitations(
  text: string,
  sourceByIndex: Map<number, SourceReference>,
  onSourceClick?: (source: SourceReference) => void,
  onRequestHighlight?: (source: SourceReference) => void,
): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = new RegExp(CITATION_REGEX.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const claim = sentenceAround(text, match.index);
    const nums = parseCitationNumbers(match[1]);
    // Group consecutive citations by document.
    const groups: number[][] = [];
    for (const n of nums) {
      const docId = sourceByIndex.get(n)?.docId;
      const prev = groups.length > 0 ? groups[groups.length - 1] : null;
      const prevDocId = prev && sourceByIndex.get(prev[0])?.docId;
      if (prev && docId && docId === prevDocId) {
        prev.push(n);
      } else {
        groups.push([n]);
      }
    }
    parts.push(
      <span key={`cite-${match.index}`} className="citation-group">
        {groups.map((group, gi) => (
          <React.Fragment key={`g-${gi}`}>
            {gi > 0 && ' '}
            <span className="citation-doc-group">
              {group.map((n, i) => (
                <React.Fragment key={n}>
                  {i > 0 && <span>, </span>}
                  <InlineCitation
                    num={n}
                    source={sourceByIndex.get(n)}
                    onClick={onSourceClick}
                    claim={claim}
                    onRequestHighlight={onRequestHighlight}
                  />
                </React.Fragment>
              ))}
            </span>
          </React.Fragment>
        ))}
      </span>,
    );
    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

/** Recursively walk React children and replace citation text patterns. */
function transformChildren(
  children: React.ReactNode,
  sourceByIndex: Map<number, SourceReference>,
  onSourceClick?: (source: SourceReference) => void,
  onRequestHighlight?: (source: SourceReference) => void,
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child !== 'string') return child;
    return replaceCitations(child, sourceByIndex, onSourceClick, onRequestHighlight);
  });
}

export const CitedMarkdown: React.FC<{
  content: string;
  sources: SourceReference[];
  onSourceClick?: (source: SourceReference) => void;
  // Called when a hover card opens on a citation with no highlights yet.
  onRequestHighlight?: (source: SourceReference) => void;
}> = ({ content, sources, onSourceClick, onRequestHighlight }) => {
  const sourceByIndex = useMemo(() => {
    const map = new Map<number, SourceReference>();
    sources.forEach((s) => {
      if (s.index != null) map.set(s.index, s);
    });
    return map;
  }, [sources]);

  const components = useMemo(
    () => ({
      p: ({ children, ...props }: any) => (
        <p {...props}>{transformChildren(children, sourceByIndex, onSourceClick, onRequestHighlight)}</p>
      ),
      li: ({ children, ...props }: any) => (
        <li {...props}>{transformChildren(children, sourceByIndex, onSourceClick, onRequestHighlight)}</li>
      ),
      strong: ({ children, ...props }: any) => (
        <strong {...props}>{transformChildren(children, sourceByIndex, onSourceClick, onRequestHighlight)}</strong>
      ),
      em: ({ children, ...props }: any) => (
        <em {...props}>{transformChildren(children, sourceByIndex, onSourceClick, onRequestHighlight)}</em>
      ),
    }),
    [sourceByIndex, onSourceClick, onRequestHighlight],
  );

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
};

interface DocGroup {
  title: string;
  docId: string;
  indices: number[];
  page?: number;
}

/** Group cited sources by document for a references / footnotes list. */
export const groupCitedSourcesByDoc = (
  content: string,
  sources: SourceReference[],
): DocGroup[] => {
  const cited = extractCitedNumbers(content);
  const sourceByIndex = new Map<number, SourceReference>();
  sources.forEach((s) => {
    if (s.index != null) sourceByIndex.set(s.index, s);
  });
  const groupMap = new Map<string, DocGroup>();
  const order: string[] = [];
  cited.forEach((num) => {
    const src = sourceByIndex.get(num);
    if (!src) return;
    const key = src.docId || src.title;
    if (!groupMap.has(key)) {
      groupMap.set(key, { title: src.title, docId: src.docId, indices: [], page: src.page });
      order.push(key);
    }
    groupMap.get(key)!.indices.push(num);
  });
  return order.map((k) => groupMap.get(k)!);
};

const RefGroupLinks: React.FC<{
  group: DocGroup;
  sources: SourceReference[];
  onSourceClick?: (source: SourceReference) => void;
  hidePages?: boolean;
}> = ({ group, sources, onSourceClick, hidePages }) => (
  <div className="ai-summary-ref-group">
    {group.title}
    {' | '}
    {group.indices.map((idx, i) => (
      <React.Fragment key={idx}>
        {i > 0 && ' '}
        <a
          href="#"
          className="ai-summary-ref-link"
          onClick={(e) => {
            e.preventDefault();
            const src = sources.find((s) => s.index === idx);
            if (src && onSourceClick) onSourceClick(src);
          }}
        >
          <span className="citation-doc-group">
            <span className="ai-summary-citation">{idx}</span>
          </span>
          {!hidePages && group.page ? ` p.${group.page}` : ''}
        </a>
      </React.Fragment>
    ))}
  </div>
);

/** References for one block of cited content, grouped by document. */
export const CitedReferences: React.FC<{
  content: string;
  sources: SourceReference[];
  onSourceClick?: (source: SourceReference) => void;
  collapsible?: boolean;
  labelPrefix?: string;
  className?: string;
  // Document-level citations (one number per document): list the documents
  // without page numbers.
  hidePages?: boolean;
}> = ({
  content,
  sources,
  onSourceClick,
  collapsible = true,
  labelPrefix = 'References',
  className = '',
  hidePages = false,
}) => {
  const [expanded, setExpanded] = useState(!collapsible);
  const groups = useMemo(() => groupCitedSourcesByDoc(content, sources), [content, sources]);
  if (groups.length === 0) return null;

  const containerClass = className
    ? `ai-summary-references ${className}`
    : 'ai-summary-references';
  const list = (
    <div className="assistant-refs-list">
      {groups.map((group) => (
        <RefGroupLinks
          key={group.docId || group.title}
          group={group}
          sources={sources}
          onSourceClick={onSourceClick}
          hidePages={hidePages}
        />
      ))}
    </div>
  );

  if (!collapsible) return <div className={containerClass}>{list}</div>;

  return (
    <div className={containerClass}>
      <button className="assistant-refs-toggle" onClick={() => setExpanded(!expanded)}>
        <span className="assistant-refs-toggle-icon">{expanded ? '▾' : '▸'}</span>
        {labelPrefix} ({groups.length} {groups.length === 1 ? 'document' : 'documents'})
      </button>
      {expanded && list}
    </div>
  );
};
